import type { ConstructorGrid, GridFragment, GridPlacement } from '../types'
import { validatePlacementBatch } from './constructor'
import { generateId, getLettersFromText } from './pairings'

function placementExtent(placement: GridPlacement): {
  rowStart: number
  colStart: number
  rowEnd: number
  colEnd: number
} {
  const length = getLettersFromText(placement.wordAText).length
  if (placement.direction === 'across') {
    return {
      rowStart: placement.row,
      colStart: placement.col,
      rowEnd: placement.row + 1,
      colEnd: placement.col + length
    }
  }
  return {
    rowStart: placement.row,
    colStart: placement.col,
    rowEnd: placement.row + length,
    colEnd: placement.col + 1
  }
}

// Build a fragment from placements: shift coords so the bbox starts at (0,0).
export function fragmentFromPlacements(
  placements: GridPlacement[],
  name: string
): GridFragment | null {
  if (placements.length === 0) return null

  let minRow = Infinity
  let minCol = Infinity
  let maxRow = -Infinity
  let maxCol = -Infinity

  for (const placement of placements) {
    const ext = placementExtent(placement)
    if (ext.rowStart < minRow) minRow = ext.rowStart
    if (ext.colStart < minCol) minCol = ext.colStart
    if (ext.rowEnd > maxRow) maxRow = ext.rowEnd
    if (ext.colEnd > maxCol) maxCol = ext.colEnd
  }

  const normalized: GridPlacement[] = placements.map(p => ({
    ...p,
    id: generateId(),
    row: p.row - minRow,
    col: p.col - minCol
  }))

  const now = new Date().toISOString()
  return {
    id: generateId(),
    name,
    placements: normalized,
    width: maxCol - minCol,
    height: maxRow - minRow,
    createdAt: now,
    updatedAt: now
  }
}

export type ApplyFragmentResult =
  | { success: true; grid: ConstructorGrid }
  | { success: false; error: string }

// Try to place every fragment placement at (rowOffset + p.row, colOffset + p.col).
// Delegates to validatePlacementBatch so fragments enforce the SAME constraints
// the suggestion system does: bounds, blocked cells, letter conflicts (with '□'
// wildcards), and adjacency / parallel-run rules. Fragments may legitimately be
// disconnected, so intersection isn't required.
export function tryApplyFragment(
  grid: ConstructorGrid,
  fragment: GridFragment,
  rowOffset: number,
  colOffset: number
): ApplyFragmentResult {
  const placements = fragment.placements.map(p => ({
    pairingId: p.pairingId,
    wordAText: p.wordAText,
    wordBText: p.wordBText,
    row: p.row + rowOffset,
    col: p.col + colOffset,
    direction: p.direction,
    isTempA: p.isTempA,
    isTempB: p.isTempB,
    priority: p.priority,
    id: generateId(),
  }))

  const result = validatePlacementBatch(grid, placements, { requireIntersection: false })
  if (!result.valid) {
    const first = result.reasons[0]
    return { success: false, error: first ? `${first.reason}${first.detail ? ` (${first.detail})` : ''}` : 'invalid' }
  }
  return { success: true, grid: result.grid }
}

// Scan all (row, col) offsets and return those where the fragment fits cleanly.
export function findFragmentFitPositions(
  grid: ConstructorGrid,
  fragment: GridFragment
): Array<{ row: number; col: number }> {
  const positions: Array<{ row: number; col: number }> = []
  if (fragment.placements.length === 0) return positions
  if (fragment.width > grid.width || fragment.height > grid.height) return positions

  for (let row = 0; row + fragment.height <= grid.height; row++) {
    for (let col = 0; col + fragment.width <= grid.width; col++) {
      const result = tryApplyFragment(grid, fragment, row, col)
      if (result.success) positions.push({ row, col })
    }
  }
  return positions
}

// For each fragment in the list, count fit positions on the given grid.
export function countFitsPerFragment(
  grid: ConstructorGrid | null,
  fragments: GridFragment[]
): Map<string, number> {
  const result = new Map<string, number>()
  if (!grid) {
    for (const f of fragments) result.set(f.id, 0)
    return result
  }
  for (const fragment of fragments) {
    result.set(fragment.id, findFragmentFitPositions(grid, fragment).length)
  }
  return result
}
